import { useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import API from "../utils/api";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

export default function AfterLoginPage() {
  const navigate = useNavigate();

  const [showAccountModal, setShowAccountModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [showWithdrawHistoryModal, setShowWithdrawHistoryModal] = useState(false);

  const [history, setHistory] = useState([]);
  const [withdrawHistory, setWithdrawHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [userBalance, setUserBalance] = useState({ wallet: 0, win: 0 });

  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const [selectedBet, setSelectedBet] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  const [marqueeText, setMarqueeText] = useState("Welcome to Royal King!");
  const [homescreenText, setHomescreenText] = useState("");

  const [withdrawData, setWithdrawData] = useState({
    amount: "",
    bankName: "",
    accountNumber: "",
    accountHolderName: "",
    ifscCode: "",
    upiId: "",
  });

  const [passwordData, setPasswordData] = useState({
    oldPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  // Fetch user balance on mount
  useEffect(() => {
    fetchUserBalance();
    fetchMarqueeText();
  }, []);

  // ================= FETCH MARQUEE TEXT =================
  const fetchMarqueeText = async () => {
    try {
      const response = await API.get("/auth/marquee");
      if (response.data.success) {
        setMarqueeText(response.data.marqueeText);
        setHomescreenText(response.data.homescreenText);
      }
    } catch (err) {
      console.error("Error fetching marquee text:", err);
    }
  };

  // ================= FETCH USER BALANCE =================
  const fetchUserBalance = async () => {
  try {
    const storedUser = JSON.parse(localStorage.getItem("user"));
    if (!storedUser) return;

    // Don't send userId in params, token will identify the user
  const response = await API.get("/auth/balance", {
        params: { userId: storedUser.user._id }
      });
    setUserBalance({
      wallet: response.data.wallet_balance || 0,
      win: response.data.win_balance || 0
    });

    // Update localStorage with new balance and keep the token
    const updatedUser = {
      ...storedUser,
      user: {
        ...storedUser.user,
        wallet_balance: response.data.wallet_balance,
        win_balance: response.data.win_balance
      }
    };
    localStorage.setItem("user", JSON.stringify(updatedUser));
  } catch (err) {
    console.error("Error fetching balance:", err);
    if (err.response?.status === 401) {
          alert("Session Timeout"); // ← add this temporarily

      localStorage.removeItem("user");
      window.location.href = "/";
          toast("error", "Session expired. Please log in again.");

    } else {
      toast.error("Failed to fetch balance");
    }
  }
};

  // ================= FETCH HISTORY =================
  const fetchHistory = async () => {
    try {
      setLoading(true);

      const storedUser = JSON.parse(localStorage.getItem("user"));

      const response = await API.get("/transactions/history", {
        params: {
          userId: storedUser.user._id,
          from: fromDate,
          to: toDate,
        },
      });

      setHistory(response.data);
    } catch (err) {
      console.error(err);
      toast.error("Failed to fetch history");
    } finally {
      setLoading(false);
    }
  };

  // ================= FETCH WITHDRAWAL HISTORY =================
  const fetchWithdrawHistory = async () => {
    try {
      setLoadingHistory(true);
      const storedUser = JSON.parse(localStorage.getItem("user"));
      
      const response = await API.get("/auth/withdraw/history", {
        params: { userId: storedUser.user._id }
      });
      
      if (response.data.success) {
        setWithdrawHistory(response.data.requests);
      }
    } catch (err) {
      console.error("Error fetching withdrawal history:", err);
      toast.error("Failed to fetch withdrawal history");
    } finally {
      setLoadingHistory(false);
    }
  };

  // ================= RESET FILTER =================
  const resetFilter = () => {
    setFromDate("");
    setToDate("");
    fetchHistory();
  };

  // ================= CHANGE PASSWORD =================
  const handlePasswordChange = async () => {
    try {
      if (passwordData.newPassword !== passwordData.confirmPassword) {
        toast.error("Passwords do not match");
        return;
      }

      if (passwordData.newPassword.length < 6) {
        toast.error("Password must be at least 6 characters");
        return;
      }

      const storedUser = JSON.parse(localStorage.getItem("user"));

      await API.post("/auth/change-password", {
        userId: storedUser.user._id,
        oldPassword: passwordData.oldPassword,
        newPassword: passwordData.newPassword,
      });

      toast.success("Password updated successfully");

      setShowPasswordModal(false);
      setPasswordData({
        oldPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
    } catch (err) {
      toast.error(err.response?.data?.error || "Error updating password");
    }
  };

  // ================= WITHDRAW REQUEST =================
  const handleWithdraw = async () => {
    try {
      if (!withdrawData.amount || withdrawData.amount <= 0) {
        toast.error("Please enter a valid amount");
        return;
      }

      if (withdrawData.amount < 100) {
        toast.error("Minimum withdrawal amount is ₹100");
        return;
      }

      if (withdrawData.amount > userBalance.wallet) {
        toast.error(`Insufficient wallet balance. Available: ₹${userBalance.wallet}`);
        return;
      }

      const storedUser = JSON.parse(localStorage.getItem("user"));

      const payload = {
        userId: storedUser.user._id,
        amount: parseFloat(withdrawData.amount),
      };

      if (withdrawData.method === "bank") {
        payload.bankDetails = {
          bankName: withdrawData.bankName,
          accountNumber: withdrawData.accountNumber,
          accountHolderName: withdrawData.accountHolderName,
          ifscCode: withdrawData.ifscCode
        };
      } else {
        payload.upiId = withdrawData.upiId;
      }

      const response = await API.post("/auth/withdraw/request", payload);

      toast.success(response.data.message || "Withdrawal request submitted successfully");

      // Reset form and close modal
      setWithdrawData({
        amount: "",
        bankName: "",
        accountNumber: "",
        accountHolderName: "",
        ifscCode: "",
        upiId: "",
      });
      setShowWithdrawModal(false);

      // Refresh balance and history
      fetchUserBalance();
      fetchWithdrawHistory();
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to submit withdrawal request");
    }
  };

  // Format date
  const formatDate = (date) => {
    return new Date(date).toLocaleString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get status badge class
  const getStatusBadge = (status) => {
    switch(status) {
      case 'pending':
        return 'badge bg-warning';
      case 'approved':
        return 'badge bg-success';
      case 'rejected':
        return 'badge bg-danger';
      case 'cancelled':
        return 'badge bg-secondary';
      default:
        return 'badge bg-info';
    }
  };

  return (
    <>
      <ToastContainer position="top-right" autoClose={3000} />
      
      {/* ================= MAIN ================= */}
      <section className="after-login-page">
        <div className="after-login-box position-relative">
          
          {/* Header with Close Button and Marquee */}

            <button
              className="close-btn-top-left me-2"
              onClick={() => {
                localStorage.removeItem("user");
                navigate("/", { replace: true });
              }}
            >
              ✕
            </button>
        

          <div className="login-logo text-center mb-3">
            <img src="/img/royalking-logotext.png" alt="" className="img-fluid" style={{ maxHeight: "80px" }} />
          </div>



          <div className="row g-3 g-lg-4">
            <div className="col-12">
              <button
                className="play-win-btn w-100 py-3 py-lg-4"
                onClick={() => navigate("/home", { replace: true })}
              >
                <span>🎮 Play Royal King</span>
              </button>
            </div>

            <div className="col-3">
              <button
                className="after-bottom-btn w-100"
                onClick={() => {
                  setShowAccountModal(true);
                  fetchHistory();
                }}
              >
                <span>📋</span>
                <span className="mt-1 d-block">Account</span>
              </button>
            </div>

            <div className="col-3">
              <button
                className="after-bottom-btn w-100"
                onClick={() => setShowPasswordModal(true)}
              >
                <span>🔒</span>
                <span className="mt-1 d-block">Password</span>
              </button>
            </div>

            <div className="col-3">
              <button
                className="after-bottom-btn withdraw-btn w-100"
                onClick={() => setShowWithdrawModal(true)}
              >
                <span>💸</span>
                <span className="mt-1 d-block">Transfer Point</span>
              </button>
            </div>

            {/* New: Withdrawal History Button */}
            <div className="col-3">
              <button
                className="after-bottom-btn withdraw-btn w-100"
                onClick={() => {
                  setShowWithdrawHistoryModal(true);
                  fetchWithdrawHistory();
                }}
              >
                <span>📜</span>
                <span className="mt-1 d-block">Transfer History</span>
              </button>
            </div>
          </div>
        </div>
      </section>
            <div className="marquee-container flex-grow-1">
            <div className="marquee-wrapper">
              <div className="marquee-content">
                <span className="marquee-text">{marqueeText} - {homescreenText}</span>
                <span className="marquee-text">{marqueeText} - {homescreenText}</span>
              </div>
            </div>
          </div>

      {/* ================= ACCOUNT MODAL ================= */}
      {showAccountModal && (
        <div className="result-modal" onClick={() => setShowAccountModal(false)}>
          <div
            className="result-modal-content"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: "90vw", width: "800px" }}
          >
            <div className="modal-header">
              <h3>Account History</h3>
              <button onClick={() => setShowAccountModal(false)}>×</button>
            </div>

            <div className="modal-body">
              {/* FILTER */}
              <div className="filter-row flex-wrap">
                <input
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  style={{ flex: 1, minWidth: "120px" }}
                />

                <input
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  style={{ flex: 1, minWidth: "120px" }}
                />

                <button className="ac-submit" onClick={fetchHistory}>
                  SUBMIT
                </button>

                <button className="ac-cancel" onClick={resetFilter}>
                  CANCEL
                </button>
              </div>

              {/* TABLE - Responsive */}
              <div className="account-content" style={{ overflowX: "auto" }}>
                <table className="table ac-table" style={{ minWidth: "500px" }}>
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Time</th>
                      <th>Play</th>
                      <th>Win</th>
                      <th>Balance</th>
                    </tr>
                  </thead>

                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan="5" className="text-center">Loading...</td>
                      </tr>
                    ) : history.length === 0 ? (
                      <tr>
                        <td colSpan="5" className="text-center">No Data</td>
                      </tr>
                    ) : (
                      history.map((item, i) => (
                        <tr
                          key={item.id}
                          style={{ cursor: item.type === "BET" ? "pointer" : "default" }}
                          onClick={() => {
                            if (item.type === "BET") {
                              setSelectedBet(item);
                              setShowDetailModal(true);
                            }
                          }}
                        >
                          <td>{i + 1}</td>
                          <td>
                            {new Date(item.date).toLocaleTimeString([], {
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </td>
                          <td>{item.play}</td>
                          <td style={{ color: item.status === "won" ? "#00ff00" : "#ff4d4d" }}>
                            {item.win}
                          </td>
                          <td>{item.point}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ================= WITHDRAWAL HISTORY MODAL ================= */}
      {showWithdrawHistoryModal && (
        <div className="result-modal" onClick={() => setShowWithdrawHistoryModal(false)}>
          <div
            className="result-modal-content"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: "90vw", width: "800px" }}
          >
            <div className="modal-header">
              <h3>Transfer History</h3>
              <button onClick={() => setShowWithdrawHistoryModal(false)}>×</button>
            </div>

            <div className="modal-body">
              <div className="account-content" style={{ overflowX: "auto" }}>
                <table className="table ac-table" style={{ minWidth: "600px" }}>
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Date</th>
                      {/* <th>Amount</th> */}
                      <th>Points</th>
                      <th>Status</th>
                    </tr>
                  </thead>

                  <tbody>
                    {loadingHistory ? (
                      <tr>
                        <td colSpan="6" className="text-center">Loading...</td>
                      </tr>
                    ) : withdrawHistory.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="text-center">No transfer requests found</td>
                      </tr>
                    ) : (
                      withdrawHistory.map((request, index) => (
                        <tr key={request._id}>
                          <td>{index + 1}</td>
                          <td>{formatDate(request.requestedAt)}</td>
                          <td className="fw-bold">₹{request.amount}</td>
                          
                          <td>
                            <span className={getStatusBadge(request.status)}>
                              {request.status.toUpperCase()}
                            </span>
                          </td>
                          
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

         
            </div>
          </div>
        </div>
      )}

      {/* ================= BET DETAIL MODAL ================= */}
      {showDetailModal && selectedBet && (
        <div className="result-modal" onClick={() => setShowDetailModal(false)}>
          <div
            className="result-modal-content bet-modal"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: "90vw", width: "500px" }}
          >
            <div className="modal-header">
              <h3>Bet Details</h3>
              <button onClick={() => setShowDetailModal(false)}>×</button>
            </div>

            <div className="modal-body">
              {/* SUMMARY */}
              <div className="bet-summary" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))" }}>
                <div>
                  <span>Date</span>
                  <p>{new Date(selectedBet.date).toLocaleString()}</p>
                </div>

                <div>
                  <span>Status</span>
                  <p className={`status ${selectedBet.status}`}>
                    {selectedBet.status}
                  </p>
                </div>

                <div>
                  <span>Play</span>
                  <p>₹{selectedBet.play}</p>
                </div>

                <div>
                  <span>Win</span>
                  <p>₹{selectedBet.win}</p>
                </div>

                <div>
                  <span>Balance</span>
                  <p>₹{selectedBet.point}</p>
                </div>
              </div>

              {/* CARDS */}
              {selectedBet.bet?.numbers?.length > 0 && (
                <div className="bet-section">
                  <h4>Selected Cards</h4>
                  <div className="card-grid" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(80px, 1fr))" }}>
                    {selectedBet.bet.numbers.map((n) => {
                      const [rank, suit] = n.card.split("-");
                      return (
                        <div className="bet-card-circle" key={n._id}>
                          <div className="bet-card-inner" style={{ width: "60px", height: "60px" }}>
                            <span className={`rank ${suit.toLowerCase()}`}>
                              {rank}
                            </span>
                            <img
                              src={`/img/cards/${suit}.png`}
                              alt={suit}
                              className="card-icon"
                              style={{ width: "20px" }}
                            />
                          </div>
                          <div className="bet-card-amount">₹{n.amount}</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* RESULT */}
              <div className="bet-section">
                <h4>Result</h4>
                {selectedBet.draw?.result ? (
                  <div className="result-box">
                    <div>
                      <span>Result Card</span>
                      <div className="result-suit-display">
                        {(() => {
                          const [rank, suit] = selectedBet.draw.result.card.split("-");
                          return (
                            <div className="bet-card-circle big">
                              <div className="bet-card-inner" style={{ width: "80px", height: "80px" }}>
                                <span className={`rank ${suit.toLowerCase()}`}>
                                  {rank}
                                </span>
                                <img
                                  src={`/img/cards/${suit}.png`}
                                  alt={suit}
                                  className="card-icon"
                                  style={{ width: "30px" }}
                                />
                              </div>
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                    <div>
                      <span>Declared At :</span>
                      <p className="result-time">
                        {new Date(selectedBet.draw.result.declaredAt).toLocaleTimeString()}
                      </p>
                    </div>
                  </div>
                ) : (
                  <p className="pending-text">⏳ Result not declared yet</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ================= WITHDRAW MODAL ================= */}
      {showWithdrawModal && (
        <div className="result-modal" onClick={() => setShowWithdrawModal(false)}>
          <div
            className="result-modal-content withdraw-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h3>💸 Transfer Point Request</h3>
              <button onClick={() => setShowWithdrawModal(false)}>×</button>
            </div>

            <div className="modal-body withdraw-body">
              <div className="row g-3">
                <div className="col-12">
                  <div className="balance-info">
                    <div className="balance-amount">
                      <span>Available Points: </span>
                      <strong>₹{userBalance.wallet}</strong>
                    </div>
                    <small>Minimum Point Transfer: ₹100</small>
                  </div>
                </div>
                <div className="col-12">
                  {/* <label className="label-one">Amount</label> */}
                  <label className="label-one">Points</label>
                  <input
                    type="number"
                    placeholder="Enter Points"
                    className="input-one"
                    value={withdrawData.amount}
                    onChange={(e) => setWithdrawData({ ...withdrawData, amount: e.target.value })}
                  />
                </div>
              </div>

              <div className="withdraw-form">
                <div className="withdraw-actions">
                  <button className="btn-submit" onClick={handleWithdraw}>
                    SUBMIT REQUEST
                  </button>
                  <button
                    className="btn-cancel"
                    onClick={() => setShowWithdrawModal(false)}
                  >
                    CANCEL
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ================= PASSWORD MODAL ================= */}
      {showPasswordModal && (
        <div className="result-modal" onClick={() => setShowPasswordModal(false)}>
          <div
            className="result-modal-content"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: "90vw", width: "400px" }}
          >
            <div className="modal-header">
              <h3>Change Password</h3>
              <button onClick={() => setShowPasswordModal(false)}>×</button>
            </div>

            <div className="modal-body">
              <input
                type="password"
                placeholder="Old Password"
                className="input-login mb-3"
                value={passwordData.oldPassword}
                onChange={(e) =>
                  setPasswordData({ ...passwordData, oldPassword: e.target.value })
                }
              />

              <input
                type="password"
                placeholder="New Password"
                className="input-login mb-3"
                value={passwordData.newPassword}
                onChange={(e) =>
                  setPasswordData({ ...passwordData, newPassword: e.target.value })
                }
              />

              <input
                type="password"
                placeholder="Confirm Password"
                className="input-login mb-3"
                value={passwordData.confirmPassword}
                onChange={(e) =>
                  setPasswordData({ ...passwordData, confirmPassword: e.target.value })
                }
              />

              <div className="pass-btn-box">
                <button className="pass-save" onClick={handlePasswordChange}>
                  UPDATE
                </button>

                <button
                  className="pass-cancel"
                  onClick={() => setShowPasswordModal(false)}
                >
                  CANCEL
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}